import { createReducer, on } from '@ngrx/store';

import { PLAN_EDIT_RATE_LIMITED } from '@core/models/plan-edit.model';
import * as AuthActions from '@core/store/auth/auth.actions';

import { PlanEditSessionActions } from './plan-edit-session.actions';
import * as session from './plan-edit-session.reducer';
import { PlanEditActions, PlanWatchActions } from './plan-edit.actions';
import {
  PlanEditState,
  PlanWatchState,
  initialPlanEditState,
  initialPlanWatchState
} from './plan-edit.state';
import {
  EMPTY_WATCH_OVERLAY,
  advance,
  applyWatchPatch,
  continuesLiveSession,
  feedWatch
} from './plan-watch-overlay';
import {
  WatchImpactState,
  initialWatchImpactState,
  watchImpactOnPatch,
  watchImpactOnRecomputeFailed,
  watchImpactOnSnapshot
} from './watch-impact';

const withWatch = (
  state: PlanEditState,
  watch: PlanWatchState,
  watchImpact: WatchImpactState = state.watchImpact
): PlanEditState =>
  watch === state.watch && watchImpact === state.watchImpact
    ? state
    : { ...state, watch, watchImpact };

const freshImpact = (planId: string | null): WatchImpactState => ({
  ...initialWatchImpactState,
  planId
});

/** The watch as a `watching` answer for the plan it watches leaves it. */
function watched(
  watch: PlanWatchState,
  {
    planId,
    holder,
    projections,
    live,
    snapshot
  }: ReturnType<typeof PlanWatchActions.watching>
): PlanWatchState {
  if (holder === null) return { ...initialPlanWatchState, planId, phase: 'idle' };
  if (!live || snapshot === null) {
    return {
      ...initialPlanWatchState,
      planId,
      phase: 'held-unavailable',
      holder,
      projections
    };
  }
  const base: PlanWatchState = {
    ...initialPlanWatchState,
    planId,
    phase: 'live',
    holder,
    projections,
    revision: snapshot.revision,
    recomputePending: snapshot.recomputePending,
    findings: snapshot.findings,
    // A re-watch of the same session whose snapshot carries no KPIs keeps the ones on screen.
    planKpis:
      snapshot.planKpis ??
      (continuesLiveSession(watch, holder, snapshot.revision) ? watch.planKpis : null),
    overlay: applyWatchPatch(EMPTY_WATCH_OVERLAY, snapshot.patch)
  };
  return watch.pendingPatches.reduce(advance, base);
}

export const planEditReducer = createReducer(
  initialPlanEditState,
  on(PlanEditActions.assistantHoldersChanged, (state, { holds }) => ({
    ...state,
    holders: Object.fromEntries(
      holds.map((h) => [
        h.planId,
        {
          kind: 'assistant' as const,
          tabId: h.tabId,
          editSessionId: h.editSessionId,
          dataSpaceId: h.dataSpaceId
        }
      ])
    )
  })),
  on(PlanEditActions.planRefreshed, (state, { planId, planDataRevision }) => ({
    ...state,
    lastRevision: { ...state.lastRevision, [planId]: planDataRevision }
  })),
  // Watching the same plan again keeps the patches buffered so far: an earlier watch's answer may
  // still land first, and `advance` drops whatever its snapshot already covers.
  on(PlanWatchActions.watchRequested, (state, { planId }) =>
    state.watch.planId === planId
      ? withWatch(state, { ...state.watch, awaitingSnapshot: true })
      : withWatch(
          state,
          {
            ...initialPlanWatchState,
            planId,
            phase: 'subscribing',
            awaitingSnapshot: true
          },
          freshImpact(planId)
        )
  ),
  on(PlanWatchActions.watchStopped, (state) =>
    withWatch(state, initialPlanWatchState, freshImpact(null))
  ),
  on(PlanWatchActions.watching, (state, action) => {
    if (action.planId !== state.watch.planId) return state;
    const watch = watched(state.watch, action);
    return withWatch(
      state,
      watch,
      watchImpactOnSnapshot(state.watchImpact, state.watch, watch)
    );
  }),
  // The effects watch again on every start; until that snapshot lands, a resumed session keeps the
  // overlay on screen, and a new one is held with nothing to show yet. A start by anyone but the
  // holder on screen is a new session: this tab missed the end of the last, whose edits and last
  // change are not the new holder's. A start carries no revision, so only the holder decides.
  on(PlanWatchActions.watchStarted, (state, { planId, holder }) => {
    if (planId !== state.watch.planId) return state;
    if (continuesLiveSession(state.watch, holder, state.watch.revision)) {
      return withWatch(state, { ...state.watch, holder });
    }
    if (state.watch.phase === 'live') {
      return withWatch(
        state,
        {
          ...initialPlanWatchState,
          planId,
          holder,
          phase: 'held-unavailable',
          awaitingSnapshot: state.watch.awaitingSnapshot
        },
        freshImpact(planId)
      );
    }
    return withWatch(state, { ...state.watch, holder, phase: 'held-unavailable' });
  }),
  on(
    PlanWatchActions.watchPatch,
    (state, { planId, revision, recomputePending, patch, findings, planKpis }) => {
      const watch = feedWatch(state.watch, planId, {
        revision,
        recomputePending,
        patch,
        findings,
        planKpis
      });
      return withWatch(
        state,
        watch,
        watchImpactOnPatch(state.watchImpact, state.watch, watch, patch)
      );
    }
  ),
  on(PlanWatchActions.watchEnded, (state, { planId }) =>
    planId !== state.watch.planId
      ? state
      : withWatch(
          state,
          { ...initialPlanWatchState, planId, phase: 'idle' },
          freshImpact(planId)
        )
  ),
  on(PlanWatchActions.watchRecomputeFailed, (state, { planId }) =>
    planId !== state.watch.planId || state.watch.phase !== 'live'
      ? state
      : withWatch(
          state,
          { ...state.watch, recomputeFailed: true },
          watchImpactOnRecomputeFailed(state.watchImpact)
        )
  ),
  on(PlanWatchActions.watchRejected, (state, { errorCode }) =>
    state.watch.planId === null || errorCode === PLAN_EDIT_RATE_LIMITED
      ? state
      : withWatch(state, {
          ...initialPlanWatchState,
          planId: state.watch.planId,
          phase: 'unavailable'
        })
  ),
  on(PlanEditSessionActions.editRequested, session.editRequested),
  on(PlanEditSessionActions.opened, session.opened),
  on(PlanEditSessionActions.gestureSent, session.gestureSent),
  on(PlanEditSessionActions.undoSent, session.undoSent),
  on(PlanEditSessionActions.applied, session.applied),
  on(PlanEditSessionActions.recomputed, session.recomputed),
  on(PlanEditSessionActions.rejected, session.rejected),
  on(PlanEditSessionActions.saveSent, session.saveSent),
  on(PlanEditSessionActions.saved, session.saved),
  on(PlanEditSessionActions.saveRejected, session.saveRejected),
  on(
    PlanEditSessionActions.discardRequested,
    PlanEditSessionActions.doneRequested,
    session.leftWhileDisconnected
  ),
  on(PlanEditSessionActions.closeSent, session.closeSent),
  on(PlanEditSessionActions.connectionLost, session.connectionLost),
  on(PlanEditSessionActions.resumed, session.resumed),
  on(PlanEditSessionActions.closed, session.closed),
  on(PlanEditSessionActions.handedOver, session.handedOver),
  on(PlanEditSessionActions.handedBack, session.handedBack),
  on(PlanEditSessionActions.restoredAfterReload, session.restoredAfterReload),
  // Logout closes the socket from this side, which reports no lost connection.
  on(AuthActions.logout, () => initialPlanEditState)
);
